import { Engine } from './core/engine.js';
import { World } from './core/world.js';
import { CFG, readUrlOverrides, applyQuality } from './core/config.js';
import { installHarness } from './debug/harness.js';

import { createInput } from './input/input.js';
import { createTerrain } from './world/terrain.js';
import { createProps } from './world/props.js';
import { createSky } from './render/sky.js';
import { createLighting } from './render/lighting.js';
import { createPipeline } from './render/pipeline.js';
import { createCharacters } from './chars/builder.js';
import { createCrowd } from './crowd/crowd.js';
import { createPlayer } from './game/player.js';
import { createCombat } from './combat/combat.js';
import { createImpact } from './combat/impact.js';
import { createAI } from './ai/ai.js';
import { createDirector } from './ai/director.js';
import { createVFX } from './vfx/vfx.js';
import { createCameraRig } from './camera/camera.js';
import { createAudio } from './audio/audio.js';
import { createMission } from './game/mission.js';
import { createSave } from './game/save.js';
import { createProgress } from './game/progress.js';
import { createTactics } from './ui/tactics.js';
import { createDuel } from './game/duel.js';
import { createMount, createMountView } from './game/mount.js';
import { createBases } from './world/bases.js';
import { createAnimation } from './anim/animation.js';
import { createPhysics } from './physics/bodies.js';
import { createHUD } from './ui/hud.js';

const bootEl = document.getElementById('boot');
const bootBar = document.getElementById('boot-bar');
const bootMsg = document.getElementById('boot-msg');
const setBoot = (p, msg) => { if (bootBar) bootBar.style.width = (p * 100).toFixed(0) + '%'; if (msg && bootMsg) bootMsg.textContent = msg; };

function fatal(err) {
  console.error(err);
  const el = document.getElementById('fatal');
  if (el) { el.style.display = 'grid'; el.textContent = 'FATAL\n\n' + (err?.stack || err); }
  if (window.__DW) { window.__DW.error = String(err?.stack || err); window.__DW.errors.push(String(err?.message || err)); }
}


/**
 * Non-blocking notice, bottom-centre, in the game's own palette.
 *
 * The fatal screen is for a boot that never happened. These are for the two
 * things that go wrong AFTER boot and used to be invisible to the player: the
 * GPU taking the context away, and a single system dying while everything else
 * keeps running. Both left the game looking merely broken with nothing said.
 */
let noticeEl = null;
function notice(text, tone) {
  if (typeof document === 'undefined') return;
  if (!noticeEl) {
    noticeEl = document.createElement('div');
    noticeEl.id = 'notice';
    noticeEl.style.cssText =
      'position:fixed;left:50%;bottom:34px;transform:translateX(-50%);z-index:150;max-width:min(560px,86vw);' +
      'padding:11px 18px;font:500 12px/1.5 system-ui,-apple-system,"Segoe UI",sans-serif;letter-spacing:.08em;' +
      'background:rgba(10,8,6,.92);border:1px solid #2a231a;color:#e8dcc0;text-align:center;pointer-events:none;' +
      'transition:opacity .3s ease';
    document.body.appendChild(noticeEl);
  }
  noticeEl.style.borderLeft = '2px solid ' + (tone === 'bad' ? '#c8492f' : '#c8912f');
  noticeEl.textContent = text;
  noticeEl.style.display = 'block';
  noticeEl.style.opacity = '1';
}
function clearNotice() {
  if (!noticeEl) return;
  noticeEl.style.opacity = '0';
  setTimeout(() => { if (noticeEl) noticeEl.style.display = 'none'; }, 320);
}

async function boot() {
  const url = readUrlOverrides();
  if (!url.quality) applyQuality(detectQuality());
  const canvas = document.getElementById('gl');
  const engine = new Engine(canvas);
  const world = new World(engine);
  if (url.seed) { world.rng.seed(url.seed); engine.rng.seed(url.seed); }
  // Entity UIDs stay globally monotonic; this seed only drives reproducible
  // battle-local spawn/personality keys and must be set before player.init().
  world.entities.setBattleSeed(url.seed || 20240819);
  const harness = installHarness(engine, world, { headless: !!url.headless });

  setBoot(0.1, 'raising the terrain…');
  engine.register(createInput(canvas));
  engine.register(createTerrain(url.seed || 4242));
  engine.register(createSky());
  engine.register(createLighting());
  engine.register(createProps());
  engine.register(createBases());
  engine.register(createCharacters());
  engine.register(createAnimation());
  engine.register(createCrowd());
  engine.register(createVFX());
  engine.register(createPlayer());
  engine.register(createCombat());
  engine.register(createImpact());
  engine.register(createPhysics());
  engine.register(createAI());
  // Persistence is OFF headless: a capture that read or wrote real storage
  // would make every scenario order-dependent (battle A clearing would change
  // what battle B boots into) and the determinism guarantee in CLAUDE.md with
  // it. The record itself still exists and still moves in memory, so the loop
  // probe can observe a battle boundary; it just starts from defaults.
  engine.register(createSave({ persist: !CFG.debug.headless }));
  engine.register(createMission());
  // After mission: progress reads `world.mission` for held bases and objective
  // state, and mission.js publishes it during ITS init.
  engine.register(createProgress());
  // After the AI: tactics writes squads.order through world.ai.squads, which
  // the AI publishes during ITS init.
  engine.register(createTactics());
  // After the AI: the duel reads world.ai.engaged and writes the crowd's ring
  // radius through world.ai.store, both published during the AI's init.
  engine.register(createDuel());
  // The mount is split in two: the simulation half above (ORDER.MISSION, so it
  // takes the attack input before player.js turns it into a ground swing) and
  // the visual half below (ORDER.RENDER, so it lands after `chars` has written
  // the hero's mesh from his entity). See game/mount.js for the full reason.
  engine.register(createMount());
  engine.register(createDirector());
  engine.register(createCameraRig());
  engine.register(createAudio());
  engine.register(createPipeline());
  engine.register(createMountView());
  engine.register(createHUD(document.getElementById('ui-root')));

  setBoot(0.45, 'mustering the armies…');
  await engine.initSystems(world);
  setBoot(0.9, 'sharpening blades…');

  // One warm-up frame so shaders compile before the curtain lifts.
  engine.advance(1 / 60);
  await new Promise(r => setTimeout(r, 0));

  window.__WORLD = world; window.__ENGINE = engine;
  setBoot(1, 'ready');
  if (CFG.debug.headless) {
    // No rAF runs in headless mode and page.evaluate() blocks the main thread,
    // so a CSS fade would still be on screen when the capture fires. Drop the
    // curtain synchronously, before anything can photograph it.
    bootEl?.remove();
  } else {
    bootEl?.classList.add('hidden');
    setTimeout(() => bootEl?.remove(), 700);
  }
  harness.booted = true;

  // Two failures that used to be silent. The engine detects them; saying so is
  // this file's job, since it owns the boot-time DOM.
  engine.onContextLost = () => notice('Graphics paused — the browser reclaimed the GPU. Waiting for it to come back…');
  engine.onContextRestored = () => clearNotice();
  engine.onFault = (f) => {
    if (f.disabled) notice(`The ${f.name} system stopped and has been switched off. The battle continues without it.`, 'bad');
    else notice(`The ${f.name} system hit an error. Recovering…`);
    if (!f.disabled) setTimeout(clearNotice, 4000);
  };

  if (!CFG.debug.headless) engine.start(world);
  document.title = 'Nusantara Warrior';
  return { engine, world };
}

function detectQuality() {
  const mem = navigator.deviceMemory || 4;
  const cores = navigator.hardwareConcurrency || 4;
  if (mem >= 8 && cores >= 8) return 'ultra';
  if (mem >= 8 || cores >= 8) return 'high';
  if (cores >= 4) return 'medium';
  return 'low';
}

boot().catch(fatal);
